import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useCoinsAnimation } from '../../../hooks/useCoinsAnimation';
import { useHiddenBodyOverflow } from '../../../hooks/useHiddenBodyOverflow';
import { analytics } from '../../../services/analytics';
import { logToNewRelic } from '../../../services/debug';
import { intercom } from '../../../services/intercom';
import { useStores } from '../../../stores/useStores';
import { devError } from '../../../utils/log';

import { buildClaimableRewards } from './buildRewards';
import { LoaderCover } from './LoaderCover';
import classes from './MinigamesDialog.module.scss';

export const MinigamesDialog = observer(() => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const {
    minigamesStore,
    walletStore,
    userFreeSpinsStore,
    userStore,
    metagamesStore,
    featureManagerStore,
    userSnappyGiftsStore,
  } = useStores();
  const { startAnimation } = useCoinsAnimation();

  const [isReady, setIsReady] = useState(false);

  useHiddenBodyOverflow(true);

  const onIframeLoad = () => {
    void logToNewRelic(
      'MinigamesDialog iframe loaded',
      'MinigamesDialog iframe loaded: ' +
        JSON.stringify({
          src: iframeRef.current?.src,
        }),
      'debug',
    );
  };

  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      try {
        const { type, payload } = event.data;
        switch (type) {
          // ─── Lifecycle ────────────────────────────────────────────────────────

          case 'gameReady': {
            void logToNewRelic(
              'MinigamesDialog game ready',
              'MinigamesDialog game ready: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'debug',
            );
            minigamesStore.setCurrentGameType(payload?.gameType);
            break;
          }
          case 'authenticationComplete': {
            setIsReady(true);
            break;
          }
          case 'authenticationError': {
            setIsReady(true);
            void logToNewRelic(
              'MinigamesDialog authentication error FE',
              'MinigamesDialog authentication error FE: ' +
                JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'error',
            );
            break;
          }
          case 'contactSupport': {
            intercom.handleOpenSupport();
            break;
          }
          case 'onErrorClose': {
            minigamesStore.closeMinigame();
            setIsReady(false);
            break;
          }
          case 'closeGame': {
            minigamesStore.closeMinigame();
            setIsReady(false);
            break;
          }
          case 'logMessage': {
            void logToNewRelic(
              'MinigamesDialog log message',
              'MinigamesDialog log message: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'debug',
            );
            break;
          }

          // ─── New contract ─────────────────────────────────────────────────────

          case 'playAnimation': {
            startAnimation({
              rewards: buildClaimableRewards(payload?.rewards),
              startMiniGames: false,
              hideOverlay: payload?.showBackdrop === false,
            });
            break;
          }
          case 'playAnimationAndClose': {
            startAnimation({
              rewards: buildClaimableRewards(payload?.rewards),
              hideOverlay: payload?.showBackdrop === false,
              onEnd: () => {
                minigamesStore.closeMinigame();
                setIsReady(false);
              },
            });
            break;
          }
          case 'analyticsEvent': {
            // The minigame fires a distinctly-named event (the contract's event_name), not the legacy 'widget'
            // bucket. Every event through this handler is a minigame, so event_parent is the constant
            // 'minigames'; the game (e.g. 'crown_rise') rides as event_category. event_action/event_value/
            // button_text are properties. feature_type/feature_sub_type are intentionally not sent —
            // deprecated, the data team no longer consumes them.
            if (!payload?.event_name) {
              devError('MinigamesDialog analyticsEvent missing event_name', payload);
              break;
            }
            analytics
              .sendMiniGameEvent(payload.event_name, {
                event_parent: 'minigames',
                // currentGameType is nullable — coerce to undefined so the prop is omitted, not sent as null.
                event_category: payload?.minigame_name || minigamesStore.currentGameType || undefined,
                event_action: payload?.event_action,
                event_value: payload?.event_value,
                button_text: payload?.button_text,
              })
              .catch((err) => devError('MinigamesDialog analyticsEvent sendEvent failed', err));
            break;
          }
          case 'vibrate': {
            if (window.navigator.vibrate) {
              const duration = payload?.haptic === 'soft' ? 20 : payload?.haptic === 'hard' ? 100 : 50;
              window.navigator.vibrate(duration);
            }
            break;
          }
          case 'gameError': {
            void logToNewRelic(
              'MinigamesDialog gameError',
              'MinigamesDialog gameError: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'error',
            );
            userFreeSpinsStore.markErrored();
            minigamesStore.closeMinigame(true);
            userFreeSpinsStore.closeDialog();
            setIsReady(false);
            break;
          }
        }
      } catch (e) {
        devError('MinigamesDialog eventHandler error', e);
      }
    };

    window.addEventListener('message', eventHandler);

    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, [
    minigamesStore,
    startAnimation,
    userFreeSpinsStore,
    userStore.profile?.userId,
    walletStore,
    metagamesStore,
    featureManagerStore,
    userSnappyGiftsStore,
  ]);

  return (
    <div className={classes.minigamesDialog}>
      <div className={classes.cover}>
        <div className={classes.dialog}>
          <iframe
            ref={iframeRef}
            className={classNames(classes.iframe, !isReady && classes.prepare)}
            src={minigamesStore.iframeData?.src || ''}
            title="Minigames"
            onLoad={onIframeLoad}
          />
        </div>
        {!isReady && <LoaderCover />}
      </div>
    </div>
  );
});
